import { useState, useMemo, useEffect } from "react";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  BarChart2, Plus, Pencil, Trash2, LogIn, Target, Zap,
  TrendingUp, Hash, Weight, DollarSign, Search, ChevronDown, Clock,
  LayoutGrid, List, CalendarRange, LineChart as LineChartIcon, X, GripVertical, Share2, Layers
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from "recharts";
import { format, parseISO } from "date-fns";
import { getRangeDates, calendarDayUtcStart, utcEntryDay, type RangePreset } from "@/lib/trackerx-dates";
import { useToast } from "@/hooks/use-toast";
import ShareDialog from "@/components/share-dialog";
import { type Category } from "@/shared/schema";
import { isFutureGoal } from "@/lib/goal-visibility";

// ─── Types ────────────────────────────────────────────────────────────────────

type ValueType = "number" | "weight" | "currency" | "time";
type TimeUnit = "minutes" | "hours" | "days" | "weeks" | "months";
type Period = "daily" | "weekly" | "monthly";

interface Tracker {
  id: string;
  name: string;
  category: string;
  valueType: ValueType;
  unitLabel: string | null;
  period: Period;
  goalValue: number | null;
  timeSlots: string[] | null;
  goalId: string | null;
  habitId: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface TrackerEntry {
  id: string;
  trackerXId: string;
  date: string;
  timeSlot: string | null;
  value: number;
  notes: string | null;
  createdAt: string;
}

interface WidgetCard {
  trackerId: string;
  metric: "today_total" | "streak" | "pct_goal" | "last_value";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const VALUE_TYPE_ICONS: Record<ValueType, React.ReactNode> = {
  number: <Hash className="h-3.5 w-3.5" />,
  weight: <Weight className="h-3.5 w-3.5" />,
  currency: <DollarSign className="h-3.5 w-3.5" />,
  time: <Clock className="h-3.5 w-3.5" />,
};

const VALUE_TYPE_LABELS: Record<ValueType, string> = {
  number: "Number",
  weight: "Weight",
  currency: "Currency",
  time: "Time",
};

const TIME_UNIT_OPTIONS: { value: TimeUnit; label: string }[] = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
];

const DEFAULT_SLOTS = ["Morning", "Afternoon", "Evening"];

function formatValue(value: number, valueType: ValueType, unitLabel: string | null) {
  if (valueType === "currency") return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (valueType === "weight") return `${value}${unitLabel ?? "kg"}`;
  if (valueType === "time") return `${value} ${unitLabel ?? "minutes"}`;
  return `${value}${unitLabel ? ` ${unitLabel}` : ""}`;
}

function getAllTotal(entries: TrackerEntry[]): number {
  return entries.reduce((s, e) => s + e.value, 0);
}

// ─── Date Range Filter ────────────────────────────────────────────────────────

const RANGE_PRESETS: { value: RangePreset; label: string }[] = [
  { value: "all", label: "All time" },
  { value: "day", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "quarter", label: "This quarter" },
  { value: "year", label: "This year" },
  { value: "custom", label: "Custom range" },
];

// ─── Graph Success Section ────────────────────────────────────────────────────

const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

type GraphMode = "bar" | "line" | "comparison";

function GraphSection({
  trackers,
  graphIds,
  onToggleGraphId,
  graphEntries,
  onClose,
}: {
  trackers: Tracker[];
  graphIds: string[];
  onToggleGraphId: (id: string) => void;
  graphEntries: Record<string, TrackerEntry[]>;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<GraphMode>("bar");
  const [comparisonStyle, setComparisonStyle] = useState<"line" | "bar">("line");

  const chartIds = mode === "comparison" ? graphIds.slice(0, 2) : graphIds;
  const chartTrackers = chartIds
    .map((id) => trackers.find((t) => t.id === id))
    .filter((t): t is Tracker => !!t);

  const chartData = useMemo(() => {
    const byDate: Record<string, Record<string, number>> = {};
    for (const t of chartTrackers) {
      const entries = graphEntries[t.id] ?? [];
      for (const e of entries) {
        const d = utcEntryDay(e.date as string);
        byDate[d] = byDate[d] ?? {};
        byDate[d][t.id] = (byDate[d][t.id] ?? 0) + e.value;
      }
    }
    const loggedDates = Object.keys(byDate).sort();
    if (loggedDates.length === 0) return [];
    // One point per calendar day, not per logged entry: days without a log
    // show 0 so the line drops instead of drawing a straight ramp between
    // sparse entries. Cap the fill so a huge "All time" span stays sane.
    const MAX_DAYS = 400;
    // Use UTC-anchored dates for day arithmetic (entry dates are UTC calendar
    // days); parseISO would anchor to local midnight and can shift a day.
    const first = new Date(`${loggedDates[0]}T00:00:00Z`);
    const last = new Date(`${loggedDates[loggedDates.length - 1]}T00:00:00Z`);
    const totalDays = Math.floor((last.getTime() - first.getTime()) / 86400000) + 1;
    if (totalDays > MAX_DAYS || totalDays < 1) {
      return loggedDates.map((d) => ({
        date: format(parseISO(d), "MMM d"),
        ...Object.fromEntries(chartTrackers.map((t) => [t.id, byDate[d][t.id] ?? 0])),
      }));
    }
    const rows: Record<string, string | number>[] = [];
    const cursor = new Date(first);
    for (let i = 0; i < totalDays; i++) {
      const iso = utcEntryDay(cursor);
      rows.push({
        date: format(parseISO(iso), "MMM d"),
        ...Object.fromEntries(chartTrackers.map((t) => [t.id, byDate[iso]?.[t.id] ?? 0])),
      });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return rows;
  }, [chartTrackers, graphEntries]);

  const tooManyForComparison = mode === "comparison" && graphIds.length > 2;
  const style = mode === "comparison" ? comparisonStyle : mode;

  return (
    <Card>
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <LineChartIcon className="h-4 w-4" />
            Graph Success
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center rounded-md border overflow-hidden">
              {(["bar", "line", "comparison"] as GraphMode[]).map((m) => (
                <Button
                  key={m}
                  variant={mode === m ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 rounded-none border-0 text-xs capitalize"
                  onClick={() => setMode(m)}
                >
                  {m}
                </Button>
              ))}
            </div>
            {mode === "comparison" && (
              <div className="flex items-center rounded-md border overflow-hidden">
                {(["line", "bar"] as const).map((s) => (
                  <Button
                    key={s}
                    variant={comparisonStyle === s ? "secondary" : "ghost"}
                    size="sm"
                    className="h-7 rounded-none border-0 text-xs capitalize"
                    onClick={() => setComparisonStyle(s)}
                  >
                    {s}
                  </Button>
                ))}
              </div>
            )}
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        {/* Tracker toggle chips */}
        <div className="flex flex-wrap gap-1.5">
          {trackers.map((t, i) => {
            const on = graphIds.includes(t.id);
            const disabled = !on && mode === "comparison" && graphIds.length >= 2;
            return (
              <Badge
                key={t.id}
                variant={on ? "default" : "outline"}
                className={`cursor-pointer select-none text-xs px-2 py-0.5 ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                style={on ? { backgroundColor: CHART_COLORS[graphIds.indexOf(t.id) % CHART_COLORS.length], color: "white", borderColor: "transparent" } : undefined}
                onClick={() => { if (!disabled) onToggleGraphId(t.id); }}
              >
                {t.name}
              </Badge>
            );
          })}
        </div>

        {mode === "comparison" && (
          <p className="text-xs text-muted-foreground">
            Comparison mode: pick exactly 2 trackers to compare against each other.
            {tooManyForComparison && " Only the first 2 selected are shown."}
          </p>
        )}

        {chartTrackers.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">
            Select one or more trackers above to graph their progress.
          </p>
        ) : mode === "comparison" && chartTrackers.length < 2 ? (
          <p className="text-sm text-muted-foreground text-center py-10">
            Select a second tracker to compare.
          </p>
        ) : chartData.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">
            No entries in the selected date range for these trackers.
          </p>
        ) : (
          <div className="w-full h-72">
            <ResponsiveContainer width="100%" height="100%">
              {style === "bar" ? (
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={45} />
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {chartTrackers.map((t) => (
                    <Bar key={t.id} dataKey={t.id} name={t.name} fill={CHART_COLORS[chartIds.indexOf(t.id) % CHART_COLORS.length]} radius={[3, 3, 0, 0]} />
                  ))}
                </BarChart>
              ) : (
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={45} />
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {chartTrackers.map((t) => (
                    <Line key={t.id} type="monotone" dataKey={t.id} name={t.name} stroke={CHART_COLORS[chartIds.indexOf(t.id) % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                  ))}
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Create / Edit Tracker Dialog ────────────────────────────────────────────

interface TrackerFormData {
  name: string;
  category: string;
  valueType: ValueType;
  unitLabel: string;
  timeUnit: TimeUnit;
  period: Period;
  goalValue: string;
  enableTimeSlots: boolean;
  timeSlots: string[];
  goalId: string;
  habitId: string;
  isActive: boolean;
}

const EMPTY_FORM: TrackerFormData = {
  name: "",
  category: "General",
  valueType: "number",
  unitLabel: "",
  timeUnit: "minutes",
  period: "daily",
  goalValue: "",
  enableTimeSlots: false,
  timeSlots: [...DEFAULT_SLOTS],
  goalId: "",
  habitId: "",
  isActive: true,
};

function TrackerFormDialog({
  open,
  onClose,
  editing,
  goals,
  habits,
}: {
  open: boolean;
  onClose: () => void;
  editing: Tracker | null;
  goals: { id: string; title: string }[];
  habits: { id: string; name: string }[];
}) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const [form, setForm] = useState<TrackerFormData>(EMPTY_FORM);
  const [newSlot, setNewSlot] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? {
            name: editing.name,
            category: editing.category,
            valueType: editing.valueType as ValueType,
            unitLabel: editing.valueType === "time" ? "" : (editing.unitLabel ?? ""),
            timeUnit: editing.valueType === "time"
              ? ((editing.unitLabel ?? "minutes") as TimeUnit)
              : "minutes",
            period: editing.period as Period,
            goalValue: editing.goalValue != null ? String(editing.goalValue) : "",
            enableTimeSlots: !!(editing.timeSlots?.length),
            timeSlots: editing.timeSlots?.length ? editing.timeSlots : [...DEFAULT_SLOTS],
            goalId: editing.goalId ?? "",
            habitId: editing.habitId ?? "",
            isActive: editing.isActive,
          }
        : EMPTY_FORM,
    );
    setNewSlot("");
  }, [open, editing]);

  const set = (key: keyof TrackerFormData) => (val: unknown) =>
    setForm((f) => ({ ...f, [key]: val }));

  const saveMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      editing
        ? apiRequest("PATCH", `/api/trackerx/${editing.id}`, data)
        : apiRequest("POST", "/api/trackerx", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/trackerx"] });
      toast({ title: editing ? "Tracker updated" : "Tracker created" });
      onClose();
    },
    onError: () => toast({ title: "Failed to save tracker", variant: "destructive" }),
  });

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      category: form.category.trim() || "General",
      valueType: form.valueType,
      unitLabel: form.valueType === "time" ? form.timeUnit : (form.unitLabel.trim() || null),
      period: form.period,
      goalValue: form.goalValue !== "" ? Number(form.goalValue) : null,
      timeSlots: form.enableTimeSlots ? form.timeSlots.filter(Boolean) : null,
      goalId: form.goalId || null,
      habitId: form.habitId || null,
      isActive: form.isActive,
    };
    saveMut.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-tracker-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Tracker" : "New Tracker"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => set("name")(e.target.value)}
              placeholder="e.g. Morning protein, Push-ups, Savings"
              data-testid="input-tracker-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter((c) => c.name).length === 0 ? (
                    <SelectItem value="General">General</SelectItem>
                  ) : (
                    <CategorySelectGroups
                      categories={categories}
                      renderContent={(c) => (
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color ?? undefined }} />
                          {c.name}
                        </span>
                      )}
                    />
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Period</Label>
              <Select value={form.period} onValueChange={set("period")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Value Type</Label>
              <Select value={form.valueType} onValueChange={set("valueType")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="weight">Weight</SelectItem>
                  <SelectItem value="currency">Currency</SelectItem>
                  <SelectItem value="time">Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.valueType === "time" ? (
              <div className="space-y-1">
                <Label>Time Unit</Label>
                <Select value={form.timeUnit} onValueChange={set("timeUnit")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_UNIT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Unit Label</Label>
                <Input
                  value={form.unitLabel}
                  onChange={(e) => set("unitLabel")(e.target.value)}
                  placeholder="e.g. g, reps, mins, lbs"
                />
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label>Target / Goal Value</Label>
            <Input
              type="number"
              value={form.goalValue}
              onChange={(e) => set("goalValue")(e.target.value)}
              placeholder="Leave blank for no target"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="enableSlots"
                checked={form.enableTimeSlots}
                onCheckedChange={(v) => set("enableTimeSlots")(!!v)}
              />
              <Label htmlFor="enableSlots">Split by time-of-day slots</Label>
            </div>
            {form.enableTimeSlots && (
              <div className="pl-6 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {form.timeSlots.map((slot) => (
                    <Badge key={slot} variant="secondary" className="gap-1">
                      {slot}
                      <button
                        onClick={() => set("timeSlots")(form.timeSlots.filter((s) => s !== slot))}
                        className="ml-1 text-muted-foreground hover:text-foreground"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newSlot}
                    onChange={(e) => setNewSlot(e.target.value)}
                    placeholder="Add slot…"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newSlot.trim()) {
                        set("timeSlots")([...form.timeSlots, newSlot.trim()]);
                        setNewSlot("");
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (newSlot.trim()) {
                        set("timeSlots")([...form.timeSlots, newSlot.trim()]);
                        setNewSlot("");
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Link to Goal</Label>
              <Select value={form.goalId || "none"} onValueChange={(v) => set("goalId")(v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {goals.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Link to Habit</Label>
              <Select value={form.habitId || "none"} onValueChange={(v) => set("habitId")(v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {habits.map((h) => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {editing && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="isActive"
                checked={form.isActive}
                onCheckedChange={(v) => set("isActive")(!!v)}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saveMut.isPending} data-testid="button-submit-tracker">
            {saveMut.isPending ? "Saving…" : editing ? "Save Changes" : "Create Tracker"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Log Entry Dialog ─────────────────────────────────────────────────────────

export function LogEntryDialog({
  tracker,
  open,
  onClose,
}: {
  tracker: Tracker | null;
  open: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  const [slotSelection, setSlotSelection] = useState<string>("none");
  const [specificTime, setSpecificTime] = useState(format(new Date(), "HH:mm"));
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));

  useEffect(() => {
    if (open) {
      setDate(format(new Date(), "yyyy-MM-dd"));
      setValue("");
      setNotes("");
      setSlotSelection("none");
      setSpecificTime(format(new Date(), "HH:mm"));
    }
  }, [open]);

  const logMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", `/api/trackerx/${tracker!.id}/entries`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/trackerx/all-entries"] });
      qc.invalidateQueries({ queryKey: ["/api/trackerx/totals"] });
      qc.invalidateQueries({ queryKey: ["/api/trackerx/widget-metrics"] });
      qc.invalidateQueries({ predicate: (q) => /trackerx-summary|habit-summaries/.test(String(q.queryKey[0])) });
      toast({ title: "Entry logged!" });
      setValue("");
      setNotes("");
      setSlotSelection("none");
      setDate(format(new Date(), "yyyy-MM-dd"));
      onClose();
    },
    onError: () => toast({ title: "Failed to log entry", variant: "destructive" }),
  });

  const handleLog = () => {
    if (!value.trim() || isNaN(Number(value))) {
      toast({ title: "Enter a valid number", variant: "destructive" });
      return;
    }
    const resolvedSlot =
      slotSelection === "none" ? null
      : slotSelection === "__specific__" ? specificTime
      : slotSelection;
    logMut.mutate({
      value: Number(value),
      // Calendar-date convention: send the picked day as UTC midnight.
      date: calendarDayUtcStart(date),
      timeSlot: resolvedSlot,
      notes: notes.trim() || null,
    });
  };

  if (!tracker) return null;

  // Always show Morning/Afternoon/Evening + any custom tracker slots (deduplicated)
  const DEFAULT_TIME_SLOTS = ["Morning", "Afternoon", "Evening"];
  const customSlots = (tracker.timeSlots ?? []).filter((s) => !DEFAULT_TIME_SLOTS.includes(s));
  const allSlots = [...DEFAULT_TIME_SLOTS, ...customSlots];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log — {tracker.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <div className="space-y-1">
            <Label>
              {tracker.valueType === "time"
                ? `Value (${tracker.unitLabel ?? "minutes"})`
                : `Value${tracker.unitLabel ? ` (${tracker.unitLabel})` : ""}`}
            </Label>
            <Input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0"
              autoFocus
            />
          </div>

          <div className="space-y-1">
            <Label>Time of Day</Label>
            <Select value={slotSelection} onValueChange={setSlotSelection}>
              <SelectTrigger><SelectValue placeholder="No slot" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No slot</SelectItem>
                {allSlots.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
                <SelectItem value="__specific__">Specific time…</SelectItem>
              </SelectContent>
            </Select>
            {slotSelection === "__specific__" && (
              <Input
                type="time"
                value={specificTime}
                onChange={(e) => setSpecificTime(e.target.value)}
                className="mt-2"
              />
            )}
          </div>

          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes…"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleLog} disabled={logMut.isPending}>
            {logMut.isPending ? "Logging…" : "Log Entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Tracker Card ─────────────────────────────────────────────────────────────

function TrackerCard({
  tracker,
  entries,
  allTimeTotal,
  totalLabel,
  selected,
  onToggleSelect,
  onLog,
  onShare,
  onEdit,
  onDelete,
  goals,
  habits,
}: {
  tracker: Tracker;
  entries: TrackerEntry[];
  allTimeTotal?: number;
  totalLabel?: string;
  selected?: boolean;
  onToggleSelect?: () => void;
  onLog: () => void;
  onShare: () => void;
  onEdit: () => void;
  onDelete: () => void;
  goals: { id: string; title: string }[];
  habits: { id: string; name: string }[];
}) {
  const runningTotal = allTimeTotal ?? getAllTotal(entries);
  const pct = tracker.goalValue ? Math.min(100, (runningTotal / tracker.goalValue) * 100) : null;
  const linkedGoal = tracker.goalId ? goals.find((g) => g.id === tracker.goalId) : null;
  const linkedHabit = tracker.habitId ? habits.find((h) => h.id === tracker.habitId) : null;
  const lastEntry = entries.length > 0 ? entries[0] : null;

  return (
    <Card className={`flex flex-col gap-0 overflow-hidden ${selected ? "ring-2 ring-primary" : ""}`}>
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-start justify-between gap-2">
          {onToggleSelect && (
            <Checkbox checked={!!selected} onCheckedChange={onToggleSelect} className="mt-1 flex-none" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 mb-1">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
                {VALUE_TYPE_ICONS[tracker.valueType]}
                {VALUE_TYPE_LABELS[tracker.valueType]}
              </Badge>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {tracker.period}
              </Badge>
            </div>
            <CardTitle className="text-base leading-tight truncate">{tracker.name}</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">{tracker.category}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit} data-testid={`button-edit-tracker-${tracker.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={onDelete}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-3">
        <div>
          <div className="flex justify-between items-baseline mb-1">
            <span className="text-2xl font-bold tabular-nums">
              {formatValue(runningTotal, tracker.valueType, tracker.unitLabel)}
            </span>
            {totalLabel ? (
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{totalLabel}</span>
            ) : tracker.goalValue != null ? (
              <span className="text-xs text-muted-foreground">
                / {formatValue(tracker.goalValue, tracker.valueType, tracker.unitLabel)}
              </span>
            ) : null}
          </div>
          {pct !== null && (
            <Progress value={pct} className="h-1.5" />
          )}
        </div>

        {tracker.timeSlots?.length ? (
          <div className="flex flex-wrap gap-1">
            {tracker.timeSlots.map((slot) => {
              const slotTotal = entries
                .filter((e) => e.timeSlot === slot)
                .reduce((s, e) => s + e.value, 0);
              return (
                <Badge key={slot} variant="outline" className="text-[10px] gap-1 font-normal">
                  <span className="text-muted-foreground">{slot}</span>
                  <span className="font-semibold">{slotTotal}</span>
                </Badge>
              );
            })}
          </div>
        ) : null}

        {(linkedGoal || linkedHabit) && (
          <div className="flex flex-wrap gap-1">
            {linkedGoal && (
              <Badge className="text-[10px] gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-0">
                <Target className="h-2.5 w-2.5" />
                {linkedGoal.title}
              </Badge>
            )}
            {linkedHabit && (
              <Badge className="text-[10px] gap-1 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 border-0">
                <Zap className="h-2.5 w-2.5" />
                {linkedHabit.name}
              </Badge>
            )}
          </div>
        )}

        {lastEntry && (
          <p className="text-[10px] text-muted-foreground">
            Last: {formatValue(lastEntry.value, tracker.valueType, tracker.unitLabel)} on {format(parseISO(utcEntryDay(lastEntry.date as string)), "MMM d")}
            {lastEntry.timeSlot ? ` · ${lastEntry.timeSlot}` : ""}
          </p>
        )}

        <Button size="sm" className="w-full gap-1.5" onClick={onLog}>
          <LogIn className="h-3.5 w-3.5" />
          Log Entry
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Tracker Row (list view) ──────────────────────────────────────────────────

function TrackerRow({
  tracker,
  entries,
  allTimeTotal,
  totalLabel,
  selected,
  onToggleSelect,
  onLog,
  onShare,
  onEdit,
  onDelete,
  goals,
  habits,
}: {
  tracker: Tracker;
  entries: TrackerEntry[];
  allTimeTotal?: number;
  totalLabel?: string;
  selected?: boolean;
  onToggleSelect?: () => void;
  onLog: () => void;
  onShare: () => void;
  onEdit: () => void;
  onDelete: () => void;
  goals: { id: string; title: string }[];
  habits: { id: string; name: string }[];
}) {
  const runningTotal = allTimeTotal ?? getAllTotal(entries);
  const pct = tracker.goalValue ? Math.min(100, (runningTotal / tracker.goalValue) * 100) : null;
  const linkedGoal = tracker.goalId ? goals.find((g) => g.id === tracker.goalId) : null;
  const linkedHabit = tracker.habitId ? habits.find((h) => h.id === tracker.habitId) : null;
  const lastEntry = entries.length > 0 ? entries[0] : null;

  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors ${selected ? "bg-primary/5" : ""}`}>
      {/* Select box */}
      {onToggleSelect && (
        <Checkbox checked={!!selected} onCheckedChange={onToggleSelect} className="flex-none" />
      )}
      {/* Icon */}
      <div className="flex-none w-7 h-7 rounded-md bg-muted flex items-center justify-center text-muted-foreground">
        {VALUE_TYPE_ICONS[tracker.valueType]}
      </div>

      {/* Name + meta */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium text-sm truncate">{tracker.name}</span>
          {!tracker.isActive && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">inactive</Badge>
          )}
          {linkedGoal && (
            <Badge className="text-[10px] gap-1 px-1.5 py-0 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-0">
              <Target className="h-2.5 w-2.5" />{linkedGoal.title}
            </Badge>
          )}
          {linkedHabit && (
            <Badge className="text-[10px] gap-1 px-1.5 py-0 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 border-0">
              <Zap className="h-2.5 w-2.5" />{linkedHabit.name}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[11px] text-muted-foreground">{tracker.category} · {tracker.period}</span>
          {lastEntry && (
            <span className="text-[11px] text-muted-foreground">
              · last: {formatValue(lastEntry.value, tracker.valueType, tracker.unitLabel)} on {format(parseISO(utcEntryDay(lastEntry.date as string)), "MMM d")}
            </span>
          )}
        </div>
        {pct !== null && <Progress value={pct} className="h-1 mt-1.5 max-w-xs" />}
      </div>

      {/* Running total */}
      <div className="flex-none text-right w-24 hidden sm:block">
        <div className="text-sm font-bold tabular-nums">{formatValue(runningTotal, tracker.valueType, tracker.unitLabel)}</div>
        {totalLabel ? (
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{totalLabel}</div>
        ) : tracker.goalValue != null ? (
          <div className="text-[10px] text-muted-foreground">/ {formatValue(tracker.goalValue, tracker.valueType, tracker.unitLabel)}</div>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-none">
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs px-2" onClick={onLog}>
          <LogIn className="h-3 w-3" />
          Log
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onShare} data-testid={`button-share-tracker-${tracker.id}`}>
          <Share2 className="h-3.5 w-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit} data-testid={`button-edit-tracker-${tracker.id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={onDelete}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Widget Strip ─────────────────────────────────────────────────────────────

const WIDGET_METRIC_LABELS: Record<WidgetCard["metric"], string> = {
  today_total: "Today's total",
  streak: "Streak",
  pct_goal: "% toward goal",
  last_value: "Last entry",
};

interface WidgetMetrics {
  todayTotal: number;
  allTotal: number;
  streak: number;
  lastValue: number | null;
  lastDate: string | null;
}

function SortableWidgetCard({
  id,
  idx,
  trackerName,
  display,
  onRemove,
}: {
  id: string;
  idx: number;
  trackerName: string;
  display: { value: string; sub: string; pct?: number };
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="group relative flex-none w-36 bg-muted/40 border rounded-xl p-3 space-y-1"
      data-testid={`widget-card-${idx}`}
    >
      <button
        className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
        {...listeners}
        aria-label="Drag to reorder widget"
        data-testid={`drag-handle-widget-${idx}`}
      >
        <GripVertical className="h-3 w-3" />
      </button>
      <button
        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        aria-label="Remove widget"
        data-testid={`button-remove-widget-${idx}`}
      >
        <X className="h-3 w-3" />
      </button>
      <p className="text-[10px] font-medium text-muted-foreground truncate">{trackerName}</p>
      <p className="text-xl font-bold tabular-nums leading-none">{display.value}</p>
      {display.pct !== undefined ? (
        <div className="space-y-0.5">
          <Progress value={display.pct} className="h-1" />
          <p className="text-[10px] text-muted-foreground">{display.sub}</p>
        </div>
      ) : (
        <p className="text-[10px] text-muted-foreground">{display.sub}</p>
      )}
    </div>
  );
}

function WidgetStrip({ trackers }: { trackers: Tracker[] }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickTrackerId, setPickTrackerId] = useState("");
  const [pickMetric, setPickMetric] = useState<WidgetCard["metric"]>("today_total");

  const { data: configData } = useQuery<{ widgets: WidgetCard[] }>({
    queryKey: ["/api/trackerx/widget-config"],
    queryFn: () => apiRequest("GET", "/api/trackerx/widget-config").then((r) => r.json()),
  });
  const widgets = configData?.widgets ?? [];

  // Server-side aggregates — no row caps, accurate for high-volume trackers.
  const { data: widgetMetrics = {} } = useQuery<Record<string, WidgetMetrics>>({
    queryKey: ["/api/trackerx/widget-metrics"],
    queryFn: () => apiRequest("GET", "/api/trackerx/widget-metrics").then((r) => r.json()),
    enabled: widgets.length > 0,
  });

  const saveMut = useMutation({
    mutationFn: (next: WidgetCard[]) =>
      apiRequest("PATCH", "/api/trackerx/widget-config", { widgets: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/trackerx/widget-config"] }),
    onError: () => {
      // Roll back any optimistic reorder to the server's last known order.
      qc.invalidateQueries({ queryKey: ["/api/trackerx/widget-config"] });
      toast({ title: "Failed to save widget layout", variant: "destructive" });
    },
  });

  const addWidget = () => {
    if (!pickTrackerId) return;
    saveMut.mutate([...widgets, { trackerId: pickTrackerId, metric: pickMetric }]);
    setPickerOpen(false);
    setPickTrackerId("");
    setPickMetric("today_total");
  };

  const removeWidget = (idx: number) => saveMut.mutate(widgets.filter((_, i) => i !== idx));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Stable sortable ids for the current order (duplicates disambiguated by index).
  const sortableIds = widgets.map((w, idx) => `${w.trackerId}:${w.metric}:${idx}`);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(widgets, oldIndex, newIndex);
    // Optimistically show the new order while the PATCH is in flight.
    qc.setQueryData(["/api/trackerx/widget-config"], { widgets: next });
    saveMut.mutate(next);
  };

  const metricDisplay = (t: Tracker, metric: WidgetCard["metric"]) => {
    const m: WidgetMetrics = widgetMetrics[t.id] ?? { todayTotal: 0, allTotal: 0, streak: 0, lastValue: null, lastDate: null };
    switch (metric) {
      case "today_total":
        return { value: formatValue(m.todayTotal, t.valueType, t.unitLabel), sub: "today" };
      case "streak":
        return { value: `${m.streak} day${m.streak === 1 ? "" : "s"}`, sub: "streak" };
      case "pct_goal": {
        if (!t.goalValue) return { value: "—", sub: "no goal set" };
        const pct = Math.round(Math.min(100, (m.allTotal / t.goalValue) * 100));
        return { value: `${pct}%`, sub: "of goal", pct };
      }
      case "last_value": {
        if (m.lastValue == null) return { value: "—", sub: "no entries" };
        return {
          value: formatValue(m.lastValue, t.valueType, t.unitLabel),
          sub: m.lastDate ? format(parseISO(utcEntryDay(m.lastDate)), "MMM d") : "",
        };
      }
    }
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-none" data-testid="widget-strip">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
          {widgets.map((w, idx) => {
            const t = trackers.find((tr) => tr.id === w.trackerId);
            if (!t) return null;
            return (
              <SortableWidgetCard
                key={sortableIds[idx]}
                id={sortableIds[idx]}
                idx={idx}
                trackerName={t.name}
                display={metricDisplay(t, w.metric)}
                onRemove={() => removeWidget(idx)}
              />
            );
          })}
        </SortableContext>
      </DndContext>

      {/* Add widget card */}
      <button
        className="flex-none w-36 border border-dashed rounded-xl p-3 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors min-h-[76px]"
        onClick={() => setPickerOpen(true)}
        data-testid="button-add-widget"
      >
        <Plus className="h-4 w-4" />
        <span className="text-[10px]">Add widget</span>
      </button>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Summary Widget</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Tracker</Label>
              <Select value={pickTrackerId || "none"} onValueChange={(v) => setPickTrackerId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="select-widget-tracker"><SelectValue placeholder="Choose a tracker" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Choose a tracker…</SelectItem>
                  {trackers.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Metric</Label>
              <Select value={pickMetric} onValueChange={(v) => setPickMetric(v as WidgetCard["metric"])}>
                <SelectTrigger data-testid="select-widget-metric"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(WIDGET_METRIC_LABELS) as WidgetCard["metric"][]).map((m) => (
                    <SelectItem key={m} value={m}>{WIDGET_METRIC_LABELS[m]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)}>Cancel</Button>
            <Button onClick={addWidget} disabled={!pickTrackerId || saveMut.isPending} data-testid="button-confirm-add-widget">
              Add Widget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TrackerXPage() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingTracker, setEditingTracker] = useState<Tracker | null>(null);
  const [loggingTracker, setLoggingTracker] = useState<Tracker | null>(null);
  const [sharingTracker, setSharingTracker] = useState<Tracker | null>(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [showInactive, setShowInactive] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "card">("list");
  const [rangePreset, setRangePreset] = useState<RangePreset>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [graphOpen, setGraphOpen] = useState(false);
  const [graphIds, setGraphIds] = useState<string[]>([]);

  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [groupBy, setGroupByState] = useState<"none" | "category">("none");

  // Load groupBy preference from localStorage once userId is known
  useEffect(() => {
    try {
      const saved = localStorage.getItem(userLsKey("trackerxGroupBy", userId));
      if (saved === "category" || saved === "none") setGroupByState(saved);
    } catch {}
  }, [userId]);

  const setGroupBy = (val: "none" | "category") => {
    setGroupByState(val);
    try { localStorage.setItem(userLsKey("trackerxGroupBy", userId), val); } catch {}
  };

  const range = getRangeDates(rangePreset, customFrom, customTo);
  const rangeActive = rangePreset !== "all" && (!!range.from || !!range.to);
  const rangeParams = new URLSearchParams();
  if (range.from) rangeParams.set("from", range.from);
  if (range.to) rangeParams.set("to", range.to);
  const rangeQs = rangeParams.toString();

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleGraphId = (id: string) =>
    setGraphIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const { data: trackers = [] } = useQuery<Tracker[]>({
    queryKey: ["/api/trackerx"],
    queryFn: () => apiRequest("GET", "/api/trackerx").then((r) => r.json()),
  });

  const { data: pageCategories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const { data: goalsRaw = [] } = useQuery<{ id: string; title: string; status: string; isFutureGoal?: boolean }[]>({
    queryKey: ["/api/goals"],
    queryFn: () => apiRequest("GET", "/api/goals").then((r) => r.json()),
  });
  const goalsData = goalsRaw.filter((g) => g.status === "active" && !isFutureGoal(g));

  const { data: habitsData = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/habits"],
    queryFn: () => apiRequest("GET", "/api/habits").then((r) => r.json()),
  });

  const entryQueries = useQuery<Record<string, TrackerEntry[]>>({
    queryKey: ["/api/trackerx/all-entries", trackers.map((t) => t.id).join(",")],
    queryFn: async () => {
      if (trackers.length === 0) return {};
      const results = await Promise.all(
        trackers.map((t) =>
          apiRequest("GET", `/api/trackerx/${t.id}/entries?limit=100`).then((r) => r.json()).then((entries: TrackerEntry[]) => [t.id, entries] as const)
        )
      );
      return Object.fromEntries(results);
    },
    enabled: trackers.length > 0,
  });

  const allEntries = entryQueries.data ?? {};

  const { data: allTotals = {} } = useQuery<Record<string, number>>({
    queryKey: ["/api/trackerx/totals"],
    queryFn: () => apiRequest("GET", "/api/trackerx/totals").then((r) => r.json()),
    enabled: trackers.length > 0,
  });

  const { data: rangeTotals = {} } = useQuery<Record<string, number>>({
    queryKey: ["/api/trackerx/totals", rangeQs],
    queryFn: () => apiRequest("GET", `/api/trackerx/totals?${rangeQs}`).then((r) => r.json()),
    enabled: trackers.length > 0 && rangeActive,
  });

  const { data: graphEntries = {} } = useQuery<Record<string, TrackerEntry[]>>({
    queryKey: ["/api/trackerx/graph-entries", graphIds.join(","), rangeQs],
    queryFn: async () => {
      const results = await Promise.all(
        graphIds.map((id) =>
          apiRequest("GET", `/api/trackerx/${id}/entries?limit=500${rangeQs ? `&${rangeQs}` : ""}`)
            .then((r) => r.json())
            .then((entries: TrackerEntry[]) => [id, entries] as const)
        )
      );
      return Object.fromEntries(results);
    },
    enabled: graphOpen && graphIds.length > 0,
  });

  // Range applies to selected records only (or all records if none selected)
  const rangeAppliesTo = (id: string) => rangeActive && (selectedIds.size === 0 || selectedIds.has(id));
  const displayTotal = (id: string) => (rangeAppliesTo(id) ? rangeTotals[id] ?? 0 : allTotals[id]);
  const rangeLabel = RANGE_PRESETS.find((p) => p.value === rangePreset)?.label;
  const displayLabel = (id: string) => (rangeAppliesTo(id) ? rangeLabel : undefined);

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/trackerx/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/trackerx"] });
      toast({ title: "Tracker deleted" });
    },
    onError: () => toast({ title: "Failed to delete tracker", variant: "destructive" }),
  });

  const filtered = useMemo(() => {
    return trackers.filter((t) => {
      if (!showInactive && !t.isActive) return false;
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [trackers, search, categoryFilter, showInactive]);

  const activeTrackers = trackers.filter((t) => t.isActive);

  // Group filtered trackers by category when groupBy="category"
  const groupedTrackers = useMemo(() => {
    if (groupBy !== "category") return null;
    const groups = new Map<string, typeof filtered>();
    for (const t of filtered) {
      const cat = t.category || "Uncategorized";
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(t);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered, groupBy]);

  return (
    <div className="w-full px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <BarChart2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-none">TrackerX</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {activeTrackers.length} active tracker{activeTrackers.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={graphOpen ? "secondary" : "outline"}
            onClick={() => setGraphOpen((v) => !v)}
            className="gap-1.5"
            size="sm"
          >
            <LineChartIcon className="h-4 w-4" />
            Graph Success
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="gap-1.5" size="sm" data-testid="button-create-tracker">
            <Plus className="h-4 w-4" />
            New Tracker
          </Button>
        </div>
      </div>

      {/* Widget Strip */}
      {activeTrackers.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            Summary Widgets
          </p>
          <WidgetStrip trackers={activeTrackers} />
        </div>
      )}

      {/* Graph Success section */}
      <Collapsible open={graphOpen}>
        <CollapsibleContent>
          <GraphSection
            trackers={trackers}
            graphIds={graphIds}
            onToggleGraphId={toggleGraphId}
            graphEntries={graphEntries}
            onClose={() => setGraphOpen(false)}
          />
        </CollapsibleContent>
      </Collapsible>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-1.5">
          <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
          <Select value={rangePreset} onValueChange={(v) => setRangePreset(v as RangePreset)}>
            <SelectTrigger className="h-8 w-36 text-sm">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {rangePreset === "custom" && (
          <div className="flex items-center gap-1.5">
            <Input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="h-8 w-36 text-sm"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="h-8 w-36 text-sm"
            />
          </div>
        )}
        {rangeActive && (
          <Badge variant="secondary" className="text-[10px] gap-1">
            {selectedIds.size === 0 ? "Range: all records" : `Range: ${selectedIds.size} selected`}
            {selectedIds.size > 0 && (
              <button onClick={() => setSelectedIds(new Set())} className="hover:text-destructive">
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </Badge>
        )}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search trackers…"
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="h-8 w-40 text-sm">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {pageCategories.filter((c) => c.name).map((c) => (
              <SelectItem key={c.id} value={c.name!}>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color ?? undefined }} />
                  {c.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={showInactive ? "secondary" : "ghost"}
          size="sm"
          className="h-8 text-xs"
          onClick={() => setShowInactive((v) => !v)}
        >
          {showInactive ? "Showing all" : "Active only"}
        </Button>

        <div className="flex items-center rounded-md border overflow-hidden">
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="icon"
            className="h-8 w-8 rounded-none border-0"
            onClick={() => setViewMode("list")}
            title="List view"
          >
            <List className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={viewMode === "card" ? "secondary" : "ghost"}
            size="icon"
            className="h-8 w-8 rounded-none border-0"
            onClick={() => setViewMode("card")}
            title="Card view"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Group By */}
        <Select value={groupBy} onValueChange={(v) => setGroupBy(v as "none" | "category")}>
          <SelectTrigger className="h-8 w-38 text-sm gap-1.5">
            <Layers className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <SelectValue placeholder="Group by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No grouping</SelectItem>
            <SelectItem value="category">By category</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tracker grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <BarChart2 className="h-10 w-10 text-muted-foreground/30 mx-auto" />
          <p className="text-sm text-muted-foreground">
            {trackers.length === 0
              ? "No trackers yet. Create your first one to start tracking granular data."
              : "No trackers match your filters."}
          </p>
          {trackers.length === 0 && (
            <Button onClick={() => setCreateOpen(true)} variant="outline" size="sm" className="gap-1.5">
              <Plus className="h-4 w-4" />
              Create Tracker
            </Button>
          )}
        </div>
      ) : groupBy === "category" && groupedTrackers ? (
        /* ── Grouped by category ───────────────────────────────────────── */
        <div className="space-y-6">
          {groupedTrackers.map(([cat, catTrackers]) => (
            <div key={cat}>
              {/* Category header */}
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{cat}</h3>
                <Badge variant="secondary" className="text-xs">{catTrackers.length}</Badge>
                <div className="flex-1 h-px bg-border" />
              </div>
              {viewMode === "list" ? (
                <div className="border rounded-lg overflow-hidden">
                  {catTrackers.map((tracker) => (
                    <TrackerRow
                      key={tracker.id}
                      tracker={tracker}
                      entries={allEntries[tracker.id] ?? []}
                      allTimeTotal={displayTotal(tracker.id)}
                      totalLabel={displayLabel(tracker.id)}
                      selected={selectedIds.has(tracker.id)}
                      onToggleSelect={() => toggleSelected(tracker.id)}
                      goals={goalsData}
                      habits={habitsData}
                      onLog={() => setLoggingTracker(tracker)}
                      onShare={() => setSharingTracker(tracker)}
                      onEdit={() => setEditingTracker(tracker)}
                      onDelete={() => {
                        if (confirm(`Delete "${tracker.name}"? This will also delete all its entries.`)) {
                          deleteMut.mutate(tracker.id);
                        }
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {catTrackers.map((tracker) => (
                    <TrackerCard
                      key={tracker.id}
                      tracker={tracker}
                      entries={allEntries[tracker.id] ?? []}
                      allTimeTotal={displayTotal(tracker.id)}
                      totalLabel={displayLabel(tracker.id)}
                      selected={selectedIds.has(tracker.id)}
                      onToggleSelect={() => toggleSelected(tracker.id)}
                      goals={goalsData}
                      habits={habitsData}
                      onLog={() => setLoggingTracker(tracker)}
                      onShare={() => setSharingTracker(tracker)}
                      onEdit={() => setEditingTracker(tracker)}
                      onDelete={() => {
                        if (confirm(`Delete "${tracker.name}"? This will also delete all its entries.`)) {
                          deleteMut.mutate(tracker.id);
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : viewMode === "list" ? (
        /* ── Flat list view ────────────────────────────────────────────── */
        <div className="border rounded-lg overflow-hidden">
          {filtered.map((tracker) => (
            <TrackerRow
              key={tracker.id}
              tracker={tracker}
              entries={allEntries[tracker.id] ?? []}
              allTimeTotal={displayTotal(tracker.id)}
              totalLabel={displayLabel(tracker.id)}
              selected={selectedIds.has(tracker.id)}
              onToggleSelect={() => toggleSelected(tracker.id)}
              goals={goalsData}
              habits={habitsData}
              onLog={() => setLoggingTracker(tracker)}
              onShare={() => setSharingTracker(tracker)}
              onEdit={() => setEditingTracker(tracker)}
              onDelete={() => {
                if (confirm(`Delete "${tracker.name}"? This will also delete all its entries.`)) {
                  deleteMut.mutate(tracker.id);
                }
              }}
            />
          ))}
        </div>
      ) : (
        /* ── Flat card view ────────────────────────────────────────────── */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((tracker) => (
            <TrackerCard
              key={tracker.id}
              tracker={tracker}
              entries={allEntries[tracker.id] ?? []}
              allTimeTotal={displayTotal(tracker.id)}
              totalLabel={displayLabel(tracker.id)}
              selected={selectedIds.has(tracker.id)}
              onToggleSelect={() => toggleSelected(tracker.id)}
              goals={goalsData}
              habits={habitsData}
              onLog={() => setLoggingTracker(tracker)}
              onShare={() => setSharingTracker(tracker)}
              onEdit={() => setEditingTracker(tracker)}
              onDelete={() => {
                if (confirm(`Delete "${tracker.name}"? This will also delete all its entries.`)) {
                  deleteMut.mutate(tracker.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      <TrackerFormDialog
        open={createOpen || editingTracker !== null}
        onClose={() => { setCreateOpen(false); setEditingTracker(null); }}
        editing={editingTracker}
        goals={goalsData}
        habits={habitsData}
      />

      <LogEntryDialog
        tracker={loggingTracker}
        open={loggingTracker !== null}
        onClose={() => setLoggingTracker(null)}
      />

      {sharingTracker && (
        <ShareDialog
          isOpen={!!sharingTracker}
          onClose={() => setSharingTracker(null)}
          itemId={sharingTracker.id}
          itemType="trackerx"
          itemTitle={sharingTracker.name}
        />
      )}
    </div>
  );
}
